// UAE VAT is 5% (Federal Tax Authority standard rate). The one owner of the rate on the web (audit DUP-13):
// the pages used to type 1.05 / 0.05 by hand. The helpers do the same arithmetic as those literals
// (x * 1.05, x / 1.05, x * 0.05 - VAT_MULTIPLIER is exactly 1.05), and leave the rounding to the caller.
export const VAT_RATE = 0.05;
export const VAT_MULTIPLIER = 1 + VAT_RATE;

/** The amount with VAT added (net -> gross). */
export const withVat = (amount) => amount * VAT_MULTIPLIER;
/** The amount without its VAT (gross -> net), e.g. what to send to Ejar, which adds the 5% back. */
export const withoutVat = (amount) => amount / VAT_MULTIPLIER;
/** The VAT on a net amount. */
export const vatOf = (amount) => amount * VAT_RATE;

// Salik / Darb: the gate toll (VAT inside) + AED 1 a trip + VAT on that dirham = +1.05 a trip, one figure
// everywhere (the engine's TRIP_SURCHARGE).
export const TRIP_FEE_AED = 1;
export const TRIP_SURCHARGE = TRIP_FEE_AED * VAT_MULTIPLIER;

// Safely parses an amount coming from a manually typed value or an uploaded
// Excel/CSV cell. Strips any stray currency symbols/commas/spaces and returns
// `null` (instead of NaN) when the value cannot be read as a number, so
// callers can exclude the row instead of letting NaN corrupt a total or PDF.
export const parseAmount = (v) => {
    const n = typeof v === 'number' ? v : parseFloat(String(v ?? '').replace(/[^0-9.-]/g, ''));
    return Number.isFinite(n) ? n : null;
};

// -- Tax-invoice arithmetic (audit FIN-12 / FIN-13 / FIN-15, owner 26 Sep 2026) ---------------------------
// Every invoice line is printed as Net / VAT 5% / Total, each rounded to fils, and an invoice's totals are the
// sums of its rounded lines (so the printed lines always add up to the printed total).

/** Rounded to 2 decimals (fils), half away from zero; the tiny nudge keeps 1.005 -> 1.01 despite binary floats. */
export const round2 = (x) => {
    const n = Number(x) || 0;
    return (Math.sign(n) * Math.round(Math.abs(n) * 100 + 1e-7)) / 100 || 0;
};

// The fine service fee: 5 % of a traffic / parking fine (and of an impound), with VAT on the fee only - the
// fine itself is a government charge passed on without VAT. The engine's fines config owns the value
// (FINES_SERVICE_FEE_PCT, default 5 - engine/fines_pipeline/config.py); the web mirrors that default for the
// invoices it prints.
export const FINE_SERVICE_FEE_RATE = 0.05;

/** A line whose amount is net: VAT 5 % on top. */
export const netLine = (net, { vat = true } = {}) => {
    const n = round2(net);
    const v = vat ? round2(n * VAT_RATE) : 0;
    return { net: n, vat: v, total: round2(n + v) };
};

/** A gross amount that has the 5 % VAT inside it (Salik, parking): VAT = total / 21. */
export const vatInclusiveLine = (gross) => {
    const total = round2(gross);
    const vat = round2((total * VAT_RATE) / VAT_MULTIPLIER);
    return { net: round2(total - vat), vat, total };
};

/** A fine billed to the customer: the fine + the 5 % service fee (net) + VAT on the fee only. */
export const fineLine = (fine) => {
    const f = round2(fine);
    const fee = round2(f * FINE_SERVICE_FEE_RATE);
    const vat = round2(fee * VAT_RATE);
    return { fine: f, fee, net: round2(f + fee), vat, total: round2(f + fee + vat) };
};

/**
 * A fine amount that is already the billed figure (fine + fee + VAT on the fee, e.g. 526.25 for a 500 fine):
 * the VAT inside it is the VAT on the fee, total x 0.0025 / 1.0525. The total is kept as given.
 */
export const billedFineLine = (billed) => {
    const total = round2(billed);
    const feeShare = FINE_SERVICE_FEE_RATE * VAT_RATE;               // 0.0025 of the fine
    const vat = round2((total * feeShare) / (1 + FINE_SERVICE_FEE_RATE + feeShare));
    return { net: round2(total - vat), vat, total };
};

/**
 * Salik trips billed to a company - the one Yelo rule (TRIP_SURCHARGE): the gate tolls already hold their VAT,
 * + AED 1.05 a trip (AED 1 + its VAT). Total = tolls + trips x 1.05; the VAT shown is the VAT inside,
 * tolls / 21 + trips x 0.05 (tolls 42.00 over 10 trips -> 52.50, VAT 2.50).
 */
export const salikLine = (tolls, trips = 1) => {
    const t = round2(tolls);
    const n = Math.max(0, Number(trips) || 0);
    const total = round2(t + n * TRIP_SURCHARGE);
    const vat = round2(round2((t * VAT_RATE) / VAT_MULTIPLIER) + n * TRIP_FEE_AED * VAT_RATE);
    return { net: round2(total - vat), vat, total };
};

/** Subtotal / VAT / Total of an invoice = the sums of its rounded lines. */
export const invoiceTotals = (lines) => {
    const sum = (k) => round2((lines || []).reduce((a, l) => a + (Number(l[k]) || 0), 0));
    return { net: sum('net'), vat: sum('vat'), total: sum('total') };
};

// -- One money format on the web (audit DUP-27) ---------------------------------------------------------------
// 2 decimals, en-AE grouping, 'AED' before the amount, an empty value as '—'.
const MONEY_FORMATS = {};
const moneyFormat = (decimals) => {
    if (!MONEY_FORMATS[decimals]) {
        MONEY_FORMATS[decimals] = new Intl.NumberFormat('en-AE', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
    }
    return MONEY_FORMATS[decimals];
};

/**
 * formatMoney(1234.5) -> 'AED 1,234.50'; formatMoney(null) -> '—'.
 * `currency: false` gives the number alone (a table column already headed AED); `empty` replaces the '—'.
 */
export const formatMoney = (value, { decimals = 2, currency = true, empty = '—' } = {}) => {
    if (value === null || value === undefined || value === '') return empty;
    const n = typeof value === 'number' ? value : parseAmount(value);
    if (n === null || !Number.isFinite(n)) return empty;
    const text = moneyFormat(decimals).format(n);
    return currency ? `AED ${text}` : text;
};
